/**
 * Generic composite/simple filter DSL.
 *
 * Mirrors the filter envelope every server-side search endpoint already
 * accepts (`/v1/users/metadata/domain/search`, `/v2/users/composer/permission/*`,
 * and the broader product search APIs). Pure values — no React, no axios,
 * no domain coupling — so this module can be imported from any layer.
 *
 * Vue parallel: filter literal constants live in
 * `RW-ERP-Service/src/types/constants/api-constants.ts` (FILTERS.COMPOSITE_FILTER,
 * FILTERS.SIMPLE_FILTER). Vue builds the structure inline at every call site.
 * On the React side we centralise it so a payload shape change is a one-file edit.
 */

import { FilterOperator } from '../../constants/filterOperators';

export const FILTER_KIND = {
  COMPOSITE: 'compositeFilter',
  SIMPLE: 'simpleFilter',
} as const;

export type LogicalOperator = 'AND' | 'OR';

/**
 * Operator strings come from the canonical `constants/filterOperators.ts`
 * registry so every search-style API in the app speaks one dialect. If the
 * backend adds a new operator, that constant is the single place to extend.
 */
export type { FilterOperator };

export interface SimpleFilter<TValue = unknown> {
  type: typeof FILTER_KIND.SIMPLE;
  attribute: string;
  operator: FilterOperator;
  value: TValue;
  negation: boolean;
}

export interface CompositeFilter {
  type: typeof FILTER_KIND.COMPOSITE;
  logicalOperator: LogicalOperator;
  filters: ReadonlyArray<SimpleFilter | CompositeFilter>;
  /**
   * Negate the whole composite. REQUIRED by the backend filter DTO —
   * every filter node (simple OR composite) must carry `negation`. The
   * `composite()` factory defaults it to `false`, so `and` / `or` /
   * `empty` / `anyOf` and every permission bucket emit it automatically.
   */
  negation: boolean;
}

export type AnyFilter = SimpleFilter | CompositeFilter;

export const simple = <TValue>(
  attribute: string,
  operator: FilterOperator,
  value: TValue,
  negation: boolean = false,
): SimpleFilter<TValue> => ({
  type: FILTER_KIND.SIMPLE,
  attribute,
  operator,
  value,
  negation,
});

export const eq = <TValue>(attribute: string, value: TValue): SimpleFilter<TValue> =>
  simple(attribute, '==', value);

export const among = <TValue>(
  attribute: string,
  values: ReadonlyArray<TValue>,
): SimpleFilter<ReadonlyArray<TValue>> => simple(attribute, 'among', values);

export const contains = (
  attribute: string,
  value: string,
): SimpleFilter<string> => simple(attribute, 'contains', value);

const composite = (
  logicalOperator: LogicalOperator,
  filters: ReadonlyArray<AnyFilter>,
): CompositeFilter => ({
  type: FILTER_KIND.COMPOSITE,
  logicalOperator,
  filters,
  // Backend requires `negation` on every filter node. Composites are
  // never negated by the current builders, so default to false.
  negation: false,
});

export const and = (...filters: ReadonlyArray<AnyFilter>): CompositeFilter =>
  composite('AND', filters);

export const or = (...filters: ReadonlyArray<AnyFilter>): CompositeFilter =>
  composite('OR', filters);

/**
 * `negation` is a property of the underlying simple filter — we can't wrap a
 * composite. Callers pass a simple filter; the helper returns a new one with
 * `negation` flipped. Matches the way Vue applies it inline.
 */
export const not = <TValue>(filter: SimpleFilter<TValue>): SimpleFilter<TValue> => ({
  ...filter,
  negation: !filter.negation,
});

/**
 * Sugar for "attribute equals any of these values". Empty input collapses
 * to an `empty()` composite so callers don't have to special-case the
 * "no values" path — the resulting filter selects nothing under OR semantics
 * but the backend contract treats an empty composite as "no constraint",
 * which matches the v2 permission curl behaviour.
 *
 * Emits ONE `among` simple filter, NOT an OR of `==` filters.
 *
 * 2026-09-10: the OR-of-`==` form this used to build was silently answered
 * for only the FIRST value by `POST /api/v2/users/composer/permission`, so
 * every multi-value permission filter under-reported. Verified live against
 * dev users-service (`192.168.16.211:8002`), same user, same session:
 *
 *   routePermissions, OR of 3 `==` routeIds  → 1 route returned
 *   routePermissions, `among` the same 3     → 3 routes returned
 *   domainPermissions, OR of 2 `==` domainIds → 1 domain + 1 attribute set
 *   domainPermissions, `among` the same 2     → 2 domains + 2 attribute sets
 *
 * So the collapse is not route-specific — it hit every bucket this helper
 * feeds (`routeIds`, `domainIds`, `operationIds`, `workflowIds`, `stageIds`,
 * `sectionIds`). `among` is also what the rest of the app already sends for
 * multi-value narrowing (e.g. `features/ceoPricing/api/ceoColumnSettingsApi.ts`),
 * so this aligns the permission layer with the established dialect rather
 * than inventing one.
 *
 * A single value still goes out as `among` with a one-element array — the
 * backend treats the two identically, and keeping one shape means the
 * one-vs-many boundary can't regress into the collapse again.
 */
export const anyOf = <TValue>(
  attribute: string,
  values: ReadonlyArray<TValue>,
): CompositeFilter => {
  if (values.length === 0) return empty();
  return or(among(attribute, values));
};

/** Empty AND-composite. Backend convention: "no constraint, return all". */
export const empty = (): CompositeFilter => composite('AND', []);

export const isCompositeFilter = (f: AnyFilter): f is CompositeFilter =>
  f.type === FILTER_KIND.COMPOSITE;

export const isSimpleFilter = (f: AnyFilter): f is SimpleFilter =>
  f.type === FILTER_KIND.SIMPLE;

/**
 * True when the filter contributes no constraint to the request.
 * Useful when the caller wants to decide whether to send a bucket at all.
 */
export const isEmptyFilter = (f: AnyFilter): boolean =>
  isCompositeFilter(f) && f.filters.length === 0;
